import { useMemo, useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Grid,
  Chip,
  Alert,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Divider,
  Stack,
  Link as MuiLink,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useTenant } from '../providers/TenantProvider.js';
import { db, functions } from '../firebase.js';
import { httpsCallable } from 'firebase/functions';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import type { Member, MemberStatus, CheckIn } from '../lib/db/types.js';
import type { Invoice, InvoiceStatus, Plan as DbPlan, Subscription, SubscriptionStatus } from '../lib/db/types.js';

interface MemberWithId extends Member {
  id: string;
}

interface CheckInWithId extends CheckIn {
  id: string;
}

interface SubscriptionWithId extends Subscription {
  id: string;
  planId?: string | null;
  cancelAtPeriodEnd?: boolean;
}

interface InvoiceWithId extends Invoice {
  id: string;
  amountDue?: number;
  amountPaid?: number;
  total: number;
  hostedInvoiceUrl?: string;
}

interface Plan extends DbPlan {
  id: string;
  stripePriceId?: string;
}

const ACTIVE_SUBSCRIPTION_STATUSES: SubscriptionStatus[] = ['active', 'trialing', 'past_due'];

function timestampToDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'object') {
    const timestamp = value as { toDate?: () => Date; seconds?: number };
    if (typeof timestamp.toDate === 'function') return timestamp.toDate();
    if (typeof timestamp.seconds === 'number') return new Date(timestamp.seconds * 1000);
  }
  return null;
}

function formatDate(value: unknown, options?: Intl.DateTimeFormatOptions): string {
  const date = timestampToDate(value);
  if (!date) return 'N/A';
  return date.toLocaleDateString('en-US', options);
}

function formatCurrency(amount?: number, currency = 'USD'): string {
  if (typeof amount !== 'number') return 'N/A';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount / 100);
  } catch {
    return `$${(amount / 100).toFixed(2)}`;
  }
}

function byTimestampDesc<T>(items: T[], getValue: (item: T) => unknown): T[] {
  return [...items].sort((left, right) => {
    const leftTime = timestampToDate(getValue(left))?.getTime() || 0;
    const rightTime = timestampToDate(getValue(right))?.getTime() || 0;
    return rightTime - leftTime;
  });
}

function getPlanName(subscription: SubscriptionWithId | null, plans: Plan[]): string {
  if (!subscription?.planId) return 'Plan not matched';
  const plan = plans.find((candidate) => (
    candidate.id === subscription.planId ||
    candidate.stripeProductId === subscription.planId ||
    candidate.stripePriceId === subscription.planId ||
    candidate.priceIds?.some((price) => price.id === subscription.planId)
  ));
  return plan?.name || 'Plan not matched';
}

function getInvoiceAmount(invoice: InvoiceWithId | null): number | undefined {
  if (!invoice) return undefined;
  return invoice.amountPaid ?? invoice.amountDue ?? invoice.total;
}

function invoiceDisplayDate(invoice: InvoiceWithId | null): unknown {
  if (!invoice) return null;
  return invoice.paidAt || invoice.dueDate || invoice.createdAt;
}

export function MemberProfilePage() {
  const { memberId } = useParams<{ memberId: string }>();
  const { tenantId, role, user } = useTenant();
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberWithId | null>(null);
  const [checkIns, setCheckIns] = useState<CheckInWithId[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithId[]>([]);
  const [invoices, setInvoices] = useState<InvoiceWithId[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [processingSubscription, setProcessingSubscription] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    barcodeId: '',
    status: 'active' as MemberStatus,
  });
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [checkingIn, setCheckingIn] = useState(false);

  const canEdit = role === 'owner' || role === 'manager';

  // Load member and check-ins
  useEffect(() => {
    if (!tenantId || !memberId) return;

    const loadData = async () => {
      try {
        setLoading(true);

        // Load member
        const memberDoc = await getDoc(doc(db, 'members', memberId));
        if (!memberDoc.exists()) {
          setError('Member not found');
          return;
        }

        const memberData = { id: memberDoc.id, ...memberDoc.data() } as MemberWithId;
        
        // Verify tenant ownership
        if (memberData.tenantId !== tenantId) {
          setError('Access denied');
          return;
        }

        setMember(memberData);
        setFormData({
          firstName: memberData.contact.firstName,
          lastName: memberData.contact.lastName,
          email: memberData.contact.email,
          phone: memberData.contact.phone || '',
          barcodeId: memberData.barcodeId || '',
          status: memberData.status,
        });

        // Load check-ins
        const checkInsQuery = query(
          collection(db, 'checkins'),
          where('tenantId', '==', tenantId),
          where('memberId', '==', memberId),
          orderBy('timestamp', 'desc')
        );
        const checkInsSnapshot = await getDocs(checkInsQuery);
        const checkInsData = checkInsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as CheckInWithId[];
        setCheckIns(checkInsData);

        // Load subscription history
        const subscriptionsQuery = query(
          collection(db, 'subscriptions'),
          where('tenantId', '==', tenantId),
          where('memberId', '==', memberId)
        );
        const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
        const subscriptionsData = subscriptionsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as SubscriptionWithId[];
        setSubscriptions(
          byTimestampDesc(
            subscriptionsData,
            (subscription) => subscription.currentPeriodEnd || subscription.createdAt
          )
        );

        // Load invoice history
        const invoicesQuery = query(
          collection(db, 'invoices'),
          where('tenantId', '==', tenantId),
          where('memberId', '==', memberId)
        );
        const invoicesSnapshot = await getDocs(invoicesQuery);
        const invoicesData = invoicesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as InvoiceWithId[];
        setInvoices(
          byTimestampDesc(
            invoicesData,
            (invoice) => invoice.paidAt || invoice.dueDate || invoice.createdAt
          )
        );

        // Load available plans
        const plansQuery = query(
          collection(db, 'plans'),
          where('tenantId', '==', tenantId),
          where('active', '==', true)
        );
        const plansSnapshot = await getDocs(plansQuery);
        const plansData = plansSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Plan[];
        setPlans(plansData);
      } catch (err) {
        console.error('Error loading member:', err);
        setError('Failed to load member data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tenantId, memberId]);

  const handleUpdateMember = async () => {
    if (!memberId || !member) return;

    try {
      const updates = {
        barcodeId: formData.barcodeId || undefined,
        contact: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone || undefined,
        },
        status: formData.status,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, 'members', memberId), updates);

      setMember({
        ...member,
        ...updates,
        updatedAt: new Date() as any,
      });

      setSuccess('Member updated successfully');
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Error updating member:', err);
      setError('Failed to update member');
    }
  };

  const handleCheckIn = async () => {
    if (!tenantId || !memberId || !member) return;

    try {
      setCheckingIn(true);

      // Create check-in record
      const checkInData = {
        tenantId,
        memberId,
        locationId: 'default', // TODO: Allow selecting location
        timestamp: serverTimestamp(),
        method: 'manual' as const,
        verifiedBy: user?.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'checkins'), checkInData);

      // Update member attendance count
      await updateDoc(doc(db, 'members', memberId), {
        attendanceCount: increment(1),
        updatedAt: serverTimestamp(),
      });

      // Add to local state
      const newCheckIn: CheckInWithId = {
        id: docRef.id,
        ...checkInData,
        timestamp: new Date() as any,
        createdAt: new Date() as any,
        updatedAt: new Date() as any,
      };
      setCheckIns([newCheckIn, ...checkIns]);
      setMember({
        ...member,
        attendanceCount: member.attendanceCount + 1,
      });

      setSuccess('Check-in recorded successfully');
    } catch (err) {
      console.error('Error recording check-in:', err);
      setError('Failed to record check-in');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleStartSubscription = async () => {
    if (!memberId || !selectedPlanId) {
      setError('Please select a plan');
      return;
    }

    const plan = plans.find((p) => p.id === selectedPlanId);
    if (!plan?.stripePriceId) {
      setError('Selected plan does not have a Stripe price configured');
      return;
    }

    try {
      setProcessingSubscription(true);
      setError(null);

      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({
        memberId,
        priceId: plan.stripePriceId,
        successUrl: `${window.location.origin}/members/${memberId}?checkout=success`,
        cancelUrl: `${window.location.origin}/members/${memberId}?checkout=cancel`,
      });

      const data = result.data as { success: boolean; sessionUrl: string };

      if (data.success && data.sessionUrl) {
        // Redirect to Stripe Checkout
        window.location.href = data.sessionUrl;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      setError(err.message || 'Failed to start subscription process');
      setProcessingSubscription(false);
    }
  };

  const handleManageBilling = async () => {
    if (!memberId || !member) return;

    if (!(member as any).currentSubscriptionId && subscriptions.length === 0) {
      setError('No active subscription found. Please start a subscription first.');
      return;
    }

    try {
      setProcessingSubscription(true);
      setError(null);

      const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');
      const result = await createPortalSession({
        memberId,
        returnUrl: `${window.location.origin}/members/${memberId}`,
      });

      const data = result.data as { success: boolean; portalUrl: string };

      if (data.success && data.portalUrl) {
        // Redirect to Stripe Customer Portal
        window.location.href = data.portalUrl;
      } else {
        setError('Failed to open billing portal');
        setProcessingSubscription(false);
      }
    } catch (err: any) {
      console.error('Error opening billing portal:', err);
      setError(err.message || 'Failed to open billing portal');
      setProcessingSubscription(false);
    }
  };

  const getStatusColor = (status: MemberStatus | SubscriptionStatus | InvoiceStatus) => {
    switch (status) {
      case 'active':
      case 'trialing':
      case 'paid':
        return 'success';
      case 'paused':
      case 'past_due':
      case 'incomplete':
      case 'draft':
      case 'open':
        return 'warning';
      case 'canceled':
      case 'unpaid':
      case 'incomplete_expired':
      case 'uncollectible':
      case 'void':
        return 'error';
      default:
        return 'default';
    }
  };

  const activeSubscription = useMemo(() => {
    if (!member) return null;
    return (
      subscriptions.find(
        (subscription) =>
          subscription.id === member.currentSubscriptionId ||
          subscription.stripeSubscriptionId === member.currentSubscriptionId
      ) ||
      subscriptions.find((subscription) => ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status)) ||
      subscriptions[0] ||
      null
    );
  }, [member, subscriptions]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>Loading member...</Typography>
      </Container>
    );
  }

  if (!member) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Member not found'}</Alert>
        <Button onClick={() => navigate('/members')} sx={{ mt: 2 }}>
          Back to Members
        </Button>
      </Container>
    );
  }

  const latestInvoice = invoices[0] || null;
  const waiverSignedAt = member.waiverSignedAt?.toDate?.()?.toLocaleString?.() || null;
  const subscriptionPlanName = getPlanName(activeSubscription, plans);
  const latestInvoiceAmount = getInvoiceAmount(latestInvoice);
  const renewalLabel = activeSubscription?.cancelAtPeriodEnd || activeSubscription?.cancelAt
    ? 'Cancels on'
    : 'Renews on';
  const renewalDate = activeSubscription?.cancelAt || activeSubscription?.currentPeriodEnd;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/members')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1">
            {member.contact.firstName} {member.contact.lastName}
          </Typography>
        </Box>
        <Chip label={member.status} color={getStatusColor(member.status)} />
        {canEdit && (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditDialogOpen(true)}>
            Edit
          </Button>
        )}
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography>{member.contact.email}</Typography>
                </Box>
                {member.contact.phone && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography>{member.contact.phone}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Member Since
                  </Typography>
                  <Typography>
                    {member.joinedAt
                      ? new Date(member.joinedAt.seconds * 1000).toLocaleDateString()
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Barcode ID
                  </Typography>
                  <Typography variant="body1" fontFamily="monospace">
                    {member.barcodeId || 'Not assigned'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Check-ins
                  </Typography>
                  <Typography variant="h4">{member.attendanceCount}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Waiver Status
                  </Typography>
                  <Typography>
                    {waiverSignedAt ? `Signed on ${waiverSignedAt}` : 'No signed waiver on file'}
                  </Typography>
                  {member.waiverSignatureId && (
                    <Typography variant="caption" color="text.secondary">
                      Signature ID: {member.waiverSignatureId}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleCheckIn}
                  disabled={checkingIn || member.status !== 'active'}
                  fullWidth
                >
                  {checkingIn ? 'Recording...' : 'Check In'}
                </Button>
                <Button variant="outlined" onClick={() => setQrDialogOpen(true)} fullWidth>
                  Show Barcode
                </Button>
                <Button variant="outlined" onClick={() => setNotesDialogOpen(true)} fullWidth>
                  View Notes
                </Button>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Subscription
                </Typography>
                {activeSubscription ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleManageBilling}
                    disabled={processingSubscription}
                    fullWidth
                  >
                    Manage Billing
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setSubscriptionDialogOpen(true)}
                    disabled={processingSubscription}
                    fullWidth
                  >
                    Start Subscription
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Summary */}
        {activeSubscription && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6">Subscription</Typography>
                  <Chip
                    label={activeSubscription.status}
                    color={getStatusColor(activeSubscription.status)}
                    size="small"
                  />
                </Stack>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Plan
                    </Typography>
                    <Typography>{subscriptionPlanName}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Started
                    </Typography>
                    <Typography>{formatDate(activeSubscription.createdAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Current period
                    </Typography>
                    <Typography>
                      {formatDate(activeSubscription.currentPeriodStart)} - {formatDate(activeSubscription.currentPeriodEnd)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {renewalLabel}
                    </Typography>
                    <Typography>{formatDate(renewalDate)}</Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Latest invoice
                    </Typography>
                    {latestInvoice ? (
                      <Stack spacing={0.75}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography>
                            {formatCurrency(latestInvoiceAmount, latestInvoice.currency)}
                          </Typography>
                          <Chip
                            label={latestInvoice.status}
                            color={getStatusColor(latestInvoice.status)}
                            size="small"
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {latestInvoice.paidAt ? 'Paid' : 'Due'} {formatDate(invoiceDisplayDate(latestInvoice))}
                        </Typography>
                        {latestInvoice.hostedInvoiceUrl && /^https:\/\//.test(latestInvoice.hostedInvoiceUrl) && (
                          <Button
                            component={MuiLink}
                            href={latestInvoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="text"
                            size="small"
                            startIcon={<OpenInNewIcon />}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            View invoice
                          </Button>
                        )}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">No invoices recorded yet</Typography>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleManageBilling}
                    disabled={processingSubscription}
                    fullWidth
                  >
                    Manage Billing
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Check-in History */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Check-in History
              </Typography>
              {checkIns.length === 0 ? (
                <Typography color="text.secondary">No check-ins yet</Typography>
              ) : (
                <List>
                  {checkIns.slice(0, 10).map((checkIn, index) => (
                    <Box key={checkIn.id}>
                      {index > 0 && <Divider />}
                      <ListItem>
                        <ListItemText
                          primary={new Date(checkIn.timestamp.seconds * 1000).toLocaleString()}
                          secondary={`Method: ${checkIn.method}`}
                        />
                      </ListItem>
                    </Box>
                  ))}
                </List>
              )}
              {checkIns.length > 10 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Showing 10 most recent check-ins
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Member Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Member</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="First Name"
              required
              value={formData.firstName}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, firstName: e.target.value })}
            />
            <TextField
              label="Last Name"
              required
              value={formData.lastName}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, lastName: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, email: e.target.value })}
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, phone: e.target.value })}
            />
            <TextField
              label="Barcode ID"
              placeholder="GYM-1234567890-ABC123"
              value={formData.barcodeId}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, barcodeId: e.target.value })}
              helperText="Unique barcode for check-ins"
            />
            <TextField
              select
              label="Status"
              value={formData.status}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFormData({ ...formData, status: e.target.value as MemberStatus })}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="canceled">Canceled</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateMember}
            disabled={!formData.firstName || !formData.lastName || !formData.email}
          >
            Update Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Member Barcode</DialogTitle>
        <DialogContent>
          <Paper sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {member?.barcodeId ? (
              <>
                <QRCodeSVG value={member.barcodeId} size={256} level="H" />
                <Typography variant="body2" color="text.secondary" align="center">
                  Scan this barcode to check in
                </Typography>
                <Typography variant="h6" fontFamily="monospace" align="center">
                  {member.barcodeId}
                </Typography>
                <Typography variant="caption" color="text.secondary" align="center">
                  Member: {member.contact.firstName} {member.contact.lastName}
                </Typography>
              </>
            ) : (
              <Alert severity="warning">
                This member does not have a barcode assigned. Barcode generation is required for check-ins.
              </Alert>
            )}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notes Dialog (Placeholder) */}
      <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Member Notes</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={8}
            fullWidth
            value={notes}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setNotes(e.target.value)}
            placeholder="Add notes about this member..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setNotesDialogOpen(false)}>
            Save Notes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Start Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a membership plan to start a subscription for {member?.contact.firstName}{' '}
            {member?.contact.lastName}.
          </Typography>
          {plans.length === 0 ? (
            <Alert severity="warning">
              No active plans available. Please create a plan in the Plans section first.
            </Alert>
          ) : (
            <TextField
              select
              label="Select Plan"
              value={selectedPlanId}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSelectedPlanId(e.target.value)}
              fullWidth
              sx={{ mt: 2 }}
            >
              {plans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.name} - ${(plan.price / 100).toFixed(2)}/month
                  {!plan.stripePriceId && ' (Stripe not configured)'}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscriptionDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleStartSubscription}
            disabled={!selectedPlanId || processingSubscription || plans.length === 0}
          >
            {processingSubscription ? 'Processing...' : 'Continue to Checkout'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
